import React, { useEffect, useState, useRef } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  CardActions,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Grid,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Fab,
  Container,
  ThemeProvider,
  createTheme,
  CssBaseline,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  Campaign as CampaignIcon,
} from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { addAds, fetchAllAds } from "../../store/actions";

// Dark theme configuration
const darkTheme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: "#6366f1",
      dark: "#4f46e5",
    },
    secondary: {
      main: "#10b981",
    },
    background: {
      default: "#0f172a",
      paper: "#1e293b",
    },
    surface: {
      main: "#334155",
    },
    text: {
      primary: "#f1f5f9",
      secondary: "#94a3b8",
    },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          backgroundColor: "#1e293b",
          border: "1px solid #334155",
          borderRadius: "12px",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            transform: "translateY(-2px)",
            boxShadow: "0 10px 25px rgba(99, 102, 241, 0.15)",
            borderColor: "#6366f1",
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: "#1e293b",
          border: "1px solid #334155",
          borderRadius: "12px",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#334155",
            },
            "&:hover fieldset": {
              borderColor: "#6366f1",
            },
          },
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 500,
        },
        contained: {
          boxShadow: "0 4px 12px rgba(99, 102, 241, 0.3)",
          "&:hover": {
            boxShadow: "0 6px 16px rgba(99, 102, 241, 0.4)",
          },
        },
      },
    },
  },
});

const AdvertisingPage = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingAd, setEditingAd] = useState(null);
  const [playingVideo, setPlayingVideo] = useState(null);
  const [videoLoadError, setVideoLoadError] = useState({});
  const [previewError, setPreviewError] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    mediaUrl: "",
    mediaType: "image",
  });

  const videoRefs = useRef({});
  const previewVideoRef = useRef(null);
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(fetchAllAds());
  }, []);

  const advertisement = useSelector((state) => state?.admin?.ads);

  // Transform API data to component format
  const ads = advertisement?.list
    ? advertisement.list.map((ad) => ({
        id: ad.id,
        name: ad.name,
        mediaUrl: ad.media_url,
        mediaType: ad.media_type,
        orgId: ad.org_id,
        createdAt: ad.createdAt,
        updatedAt: ad.updatedAt,
      }))
    : [];

  // Utility functions for URL handling
  const getYouTubeVideoId = (url) => {
    const regExp =
      /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return match && match[2].length === 11 ? match[2] : null;
  };

  const isYouTubeUrl = (url) => {
    return url.includes("youtube.com") || url.includes("youtu.be");
  };

  const isDirectVideoUrl = (url) => {
    try {
      new URL(url);
      const videoFormats = [".mp4", ".webm", ".ogg", ".mov", ".avi"];
      return videoFormats.some((format) => url.toLowerCase().includes(format));
    } catch {
      return false;
    }
  };

  const getEmbedUrl = (url) => {
    if (isYouTubeUrl(url)) {
      const videoId = getYouTubeVideoId(url);
      return videoId ? `https://www.youtube.com/embed/${videoId}` : null;
    }
    return null;
  };

  const handleInputChange = async (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "mediaUrl") {
      setPreviewError("");

      if (formData.mediaType === "video" && value.trim()) {
        if (!isDirectVideoUrl(value) && !isYouTubeUrl(value)) {
          setPreviewError(
            "Please provide either a YouTube URL or a direct video file URL (.mp4, .webm, .ogg, etc.)"
          );
        }
      }
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.mediaUrl.trim()) {
      alert("Please fill in all fields");
      return;
    }

    if (editingAd) {
      console.log("Updating ad:", editingAd.id, formData);
      setEditingAd(null);
    } else {
      console.log("Adding new ad:", formData);
      dispatch(
        addAds({
          media_type: formData.mediaType,
          media_url: formData.mediaUrl,
          name: formData.name,
        })
      );
      dispatch(fetchAllAds());
    }

    setIsModalOpen(false);
    setFormData({ name: "", mediaUrl: "", mediaType: "image" });
    setPreviewError("");
  };

  const handleEdit = (ad) => {
    setEditingAd(ad);
    setFormData({
      name: ad.name,
      mediaUrl: ad.mediaUrl,
      mediaType: ad.mediaType,
    });
    setIsModalOpen(true);
    setPreviewError("");
  };

  const handleDelete = (id) => {
    if (window.confirm("Are you sure you want to delete this ad?")) {
      console.log("Deleting ad:", id);
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingAd(null);
    setFormData({ name: "", mediaUrl: "", mediaType: "image" });
    setPreviewError("");
  };

  const toggleVideoPlay = async (id) => {
    const videoElement = videoRefs.current[id];
    if (!videoElement) return;

    try {
      if (playingVideo === id) {
        videoElement.pause();
        setPlayingVideo(null);
      } else {
        if (playingVideo && videoRefs.current[playingVideo]) {
          videoRefs.current[playingVideo].pause();
        }

        await videoElement.play();
        setPlayingVideo(id);
      }
    } catch (error) {
      console.error("Error playing video:", error);
      setVideoLoadError((prev) => ({
        ...prev,
        [id]: "Failed to play video",
      }));
    }
  };

  const handleVideoError = (id, error) => {
    console.error("Video error for", id, ":", error);
    setVideoLoadError((prev) => ({
      ...prev,
      [id]: "Failed to load video. This may be due to CORS policy or invalid URL format.",
    }));
  };

  const handleVideoEnded = (id) => {
    setPlayingVideo(null);
  };

  const handlePreviewError = (error) => {
    console.error("Preview error:", error);
    setPreviewError(
      "Failed to load media. Please check the URL format and try again."
    );
  };

  const renderVideoContent = (ad) => {
    const isYouTube = isYouTubeUrl(ad.mediaUrl);
    const isDirect = isDirectVideoUrl(ad.mediaUrl);

    if (isYouTube) {
      const embedUrl = getEmbedUrl(ad.mediaUrl);
      if (embedUrl) {
        return (
          <Box
            sx={{
              position: "relative",
              paddingBottom: "56.25%",
              height: 0,
              overflow: "hidden",
            }}
          >
            <iframe
              src={embedUrl}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                border: "none",
                borderRadius: "12px 12px 0 0",
              }}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={ad.name}
            />
          </Box>
        );
      }
    }

    if (isDirect) {
      return (
        <Box sx={{ position: "relative" }}>
          {videoLoadError[ad.id] ? (
            <Box
              sx={{
                height: 200,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                backgroundColor: "#334155",
                borderRadius: "12px 12px 0 0",
                color: "#ef4444",
                textAlign: "center",
                p: 2,
              }}
            >
              <Typography variant="body2">{videoLoadError[ad.id]}</Typography>
            </Box>
          ) : (
            <>
              <video
                ref={(el) => (videoRefs.current[ad.id] = el)}
                style={{
                  width: "100%",
                  height: "200px",
                  objectFit: "cover",
                  borderRadius: "12px 12px 0 0",
                }}
                src={ad.mediaUrl}
                muted
                playsInline
                preload="metadata"
                crossOrigin="anonymous"
                onError={(e) => handleVideoError(ad.id, e)}
                onEnded={() => handleVideoEnded(ad.id)}
                onLoadStart={() => {
                  setVideoLoadError((prev) => {
                    const newState = { ...prev };
                    delete newState[ad.id];
                    return newState;
                  });
                }}
              />
              <IconButton
                sx={{
                  position: "absolute",
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: "rgba(99, 102, 241, 0.9)",
                  color: "white",
                  "&:hover": {
                    backgroundColor: "rgba(79, 70, 229, 0.9)",
                  },
                }}
                onClick={() => toggleVideoPlay(ad.id)}
              >
                {playingVideo === ad.id ? <PauseIcon /> : <PlayIcon />}
              </IconButton>
            </>
          )}
        </Box>
      );
    }

    // Fallback for unsupported video URLs
    return (
      <Box
        sx={{
          height: 200,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#334155",
          borderRadius: "12px 12px 0 0",
          color: "#ef4444",
          textAlign: "center",
          p: 2,
        }}
      >
        <Typography variant="body2" sx={{ mb: 1 }}>
          Unsupported video format
        </Typography>
        <Typography variant="caption" sx={{ color: "#94a3b8" }}>
          Please use YouTube URLs or direct video file links
        </Typography>
      </Box>
    );
  };

  const renderPreviewContent = () => {
    if (!formData.mediaUrl) return null;

    if (formData.mediaType === "video") {
      const isYouTube = isYouTubeUrl(formData.mediaUrl);
      const isDirect = isDirectVideoUrl(formData.mediaUrl);

      if (isYouTube) {
        const embedUrl = getEmbedUrl(formData.mediaUrl);
        if (embedUrl) {
          return (
            <Box
              sx={{
                position: "relative",
                paddingBottom: "56.25%",
                height: 0,
                overflow: "hidden",
                maxHeight: 300,
              }}
            >
              <iframe
                src={embedUrl}
                style={{
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "100%",
                  height: "100%",
                  border: "1px solid #334155",
                  borderRadius: "8px",
                }}
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
                title="Preview"
              />
            </Box>
          );
        }
      }

      if (isDirect) {
        return (
          <video
            ref={previewVideoRef}
            style={{
              width: "100%",
              maxHeight: "200px",
              borderRadius: "8px",
              border: "1px solid #334155",
            }}
            src={formData.mediaUrl}
            controls
            muted
            playsInline
            preload="metadata"
            crossOrigin="anonymous"
            onError={handlePreviewError}
            onLoadStart={() => setPreviewError("")}
          />
        );
      }

      return (
        <Box
          sx={{
            height: 150,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#334155",
            borderRadius: "8px",
            border: "1px solid #334155",
            color: "#ef4444",
          }}
        >
          <Typography variant="body2">Invalid video URL format</Typography>
        </Box>
      );
    }

    return (
      <Box
        component="img"
        src={formData.mediaUrl}
        alt="Preview"
        sx={{
          width: "100%",
          maxHeight: 200,
          objectFit: "contain",
          borderRadius: 2,
          border: "1px solid #334155",
        }}
        onError={handlePreviewError}
      />
    );
  };

  return (
    <ThemeProvider theme={darkTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: "100vh",
          backgroundColor: "#0f172a",
          backgroundImage:
            "radial-gradient(circle at 25% 25%, rgba(99, 102, 241, 0.1) 0%, transparent 50%)",
        }}
      >
        <Container maxWidth="lg" sx={{ py: 4 }}>
          {/* Header */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
              <Box
                sx={{
                  p: 1.5,
                  backgroundColor: "#6366f1",
                  borderRadius: "12px",
                  mr: 2,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CampaignIcon sx={{ color: "white", fontSize: 28 }} />
              </Box>
              <Box>
                <Typography
                  variant="h4"
                  component="h1"
                  sx={{
                    fontWeight: 700,
                    color: "#f1f5f9",
                    mb: 0.5,
                  }}
                >
                  Advertisement Management
                </Typography>
                <Typography variant="body1" sx={{ color: "#94a3b8" }}>
                  Manage your advertisements and promotional content
                </Typography>
              </Box>
            </Box>
          </Box>

          {/* Ads Grid */}
          <Grid container spacing={3}>
            {ads.map((ad) => (
              <Grid item xs={12} sm={6} md={4} key={ad.id}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Box sx={{ position: "relative" }}>
                    {ad.mediaType === "video" ? (
                      renderVideoContent(ad)
                    ) : (
                      <CardMedia
                        component="img"
                        height="200"
                        image={ad.mediaUrl}
                        alt={ad.name}
                        sx={{
                          objectFit: "cover",
                          borderRadius: "12px 12px 0 0",
                        }}
                        onError={() =>
                          console.log("Image load error for:", ad.id)
                        }
                      />
                    )}
                  </Box>

                  <CardContent sx={{ flexGrow: 1, p: 3 }}>
                    <Typography
                      variant="h6"
                      component="h3"
                      sx={{
                        color: "#f1f5f9",
                        fontWeight: 600,
                        mb: 1,
                      }}
                    >
                      {ad.name}
                    </Typography>
                    <Box
                      sx={{
                        display: "inline-flex",
                        alignItems: "center",
                        backgroundColor:
                          ad.mediaType === "video" ? "#ef4444" : "#10b981",
                        color: "white",
                        px: 2,
                        py: 0.5,
                        borderRadius: "20px",
                        fontSize: "0.75rem",
                        fontWeight: 500,
                      }}
                    >
                      {ad.mediaType === "video" ? "Video Ad" : "Image Ad"}
                    </Box>
                    {ad.mediaType === "video" && (
                      <Typography
                        variant="caption"
                        sx={{
                          display: "block",
                          color: "#94a3b8",
                          mt: 1,
                        }}
                      >
                        {isYouTubeUrl(ad.mediaUrl)
                          ? "YouTube Video"
                          : "Direct Video"}
                      </Typography>
                    )}
                  </CardContent>

                  <CardActions
                    sx={{ justifyContent: "space-between", px: 3, pb: 3 }}
                  >
                    <Button
                      size="small"
                      startIcon={<EditIcon />}
                      onClick={() => handleEdit(ad)}
                      sx={{
                        color: "#6366f1",
                        "&:hover": {
                          backgroundColor: "rgba(99, 102, 241, 0.1)",
                        },
                      }}
                    >
                      Edit
                    </Button>
                    <IconButton
                      size="small"
                      onClick={() => handleDelete(ad.id)}
                      sx={{
                        color: "#ef4444",
                        "&:hover": {
                          backgroundColor: "rgba(239, 68, 68, 0.1)",
                        },
                      }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>

          {/* Floating Action Button */}
          <Fab
            color="primary"
            sx={{
              position: "fixed",
              bottom: 32,
              right: 32,
              backgroundColor: "#6366f1",
              "&:hover": {
                backgroundColor: "#4f46e5",
              },
              boxShadow: "0 8px 25px rgba(99, 102, 241, 0.4)",
            }}
            onClick={() => setIsModalOpen(true)}
          >
            <AddIcon />
          </Fab>

          {/* Add/Edit Modal */}
          <Dialog
            open={isModalOpen}
            onClose={handleCloseModal}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                color: "#f1f5f9",
                fontWeight: 600,
              }}
            >
              {editingAd ? "Edit Advertisement" : "Add New Advertisement"}
              <IconButton onClick={handleCloseModal} sx={{ color: "#94a3b8" }}>
                <CloseIcon />
              </IconButton>
            </DialogTitle>

            <form onSubmit={handleSubmit}>
              <DialogContent sx={{ pt: 2 }}>
                <TextField
                  fullWidth
                  label="Advertisement Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                  placeholder="Enter advertisement name"
                />

                <FormControl fullWidth margin="normal">
                  <InputLabel>Media Type</InputLabel>
                  <Select
                    name="mediaType"
                    value={formData.mediaType}
                    onChange={handleInputChange}
                    label="Media Type"
                  >
                    <MenuItem value="image">Image</MenuItem>
                    <MenuItem value="video">Video</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
                  label={
                    formData.mediaType === "video" ? "Video URL" : "Image URL"
                  }
                  name="mediaUrl"
                  value={formData.mediaUrl}
                  onChange={handleInputChange}
                  margin="normal"
                  required
                  placeholder={
                    formData.mediaType === "video"
                      ? "Enter YouTube URL or direct video URL"
                      : "Enter image URL"
                  }
                  helperText={
                    formData.mediaType === "video"
                      ? "Supports YouTube URLs (youtube.com, youtu.be) and direct video files (.mp4, .webm, .ogg)"
                      : "Use direct links to image files"
                  }
                />

                {formData.mediaUrl && (
                  <Box sx={{ mt: 3 }}>
                    <Typography
                      variant="body2"
                      sx={{ color: "#94a3b8", mb: 2 }}
                    >
                      Preview:
                    </Typography>

                    {previewError ? (
                      <Alert severity="error" sx={{ mb: 2 }}>
                        {previewError}
                      </Alert>
                    ) : null}

                    {renderPreviewContent()}

                    {formData.mediaType === "video" && (
                      <Typography
                        variant="caption"
                        sx={{
                          color: "#94a3b8",
                          display: "block",
                          mt: 1,
                          fontSize: "0.75rem",
                        }}
                      >
                        {isYouTubeUrl(formData.mediaUrl)
                          ? "YouTube video detected - will use embedded player"
                          : "Direct video file - will use HTML5 player"}
                      </Typography>
                    )}
                  </Box>
                )}
              </DialogContent>

              <DialogActions sx={{ px: 3, pb: 3 }}>
                <Button onClick={handleCloseModal} sx={{ color: "#94a3b8" }}>
                  Cancel
                </Button>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    backgroundColor: "#6366f1",
                    "&:hover": {
                      backgroundColor: "#4f46e5",
                    },
                  }}
                >
                  {editingAd ? "Update Advertisement" : "Add Advertisement"}
                </Button>
              </DialogActions>
            </form>
          </Dialog>
        </Container>
      </Box>
    </ThemeProvider>
  );
};

export default AdvertisingPage;
